import type { DeckStudyCard } from "@-/dev.improve.haniverse.lib.cards";
import {
  type CardFilterFacts,
  type CardFilters,
  matchesCardFilterMorphemeKind,
  matchesCardFilters,
} from "@-/dev.improve.haniverse.lib.cards.filter";
import { useCardFilterFactsByGrapheme } from "@-/dev.improve.haniverse.lib.cards.filter.facts";
import type { MorphemeGlyphTileStage } from "@-/dev.improve.haniverse.lib.morpheme.stage";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { DeckLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.deck._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useMemo } from "react";
import { deckStudyCardSubject } from "#deck-study-card-subject";
import { deriveDeckStageByFront } from "#derive-deck-stage-by-front";

/**
 * Which filter dimensions need the content/static DBs. Levels, tiers, and usage classification are
 * catalog facts; the stage dimension and the words/characters dimension are decided from the actor
 * rows already in hand.
 */
function requiresStaticCardFilterFacts(filters: CardFilters): boolean {
  return (
    filters.hskLevels !== null ||
    filters.tiers !== null ||
    filters.tbclLevels !== null ||
    filters.usageClassifications !== null
  );
}

/**
 * Facts for a grapheme when only actor-derivable dimensions are active: every catalog fact is
 * left at its "unknown" value (which `matchesCardFilters` never consults, since those dimensions
 * are unrestricted) and the stage comes from the deck's own rows.
 */
function actorOnlyCardFilterFacts(stage: MorphemeGlyphTileStage | undefined): CardFilterFacts {
  return {
    completed: false,
    hskLevel: null,
    stage: stage === undefined || stage === "unadded" ? null : stage,
    tbclLevel: null,
    tier: null,
    usageClassification: null,
  };
}

/**
 * The deck rows after the synced "Pinyin flashcards" setting and the toolbar card filters, plus
 * the per-front SRS stage those rows imply.
 *
 * The first paint is actor-DB only: with the deck's default filters (nothing restricted) no
 * content/static read is issued at all, and a stage-only filter is evaluated from the rows in hand.
 * The batched catalog facts read (`useCardFilterFactsByGrapheme`) only runs once a level / tier /
 * usage dimension is actually set — and while it settles the deck shows unfiltered rather than
 * flashing empty.
 */
export function useFilteredDeckStudyCards({ deckStudyCards }: { deckStudyCards: DeckStudyCard[] }) {
  // Card filters live on the layout toolbar so they persist across deck routes.
  const [cardFilters] = useContextSelectors(DeckLayoutContext, (c) => c.cardFilters);
  // Pronunciation (pinyin flashcard) cards are always authored, but the deck only
  // surfaces them when the user has the synced "Pinyin flashcards" setting on.
  const { hanziScript, isPinyinFlashcardsEnabled } = useActorUser();

  const unfilteredStudyCardList = useMemo(
    () =>
      isPinyinFlashcardsEnabled
        ? deckStudyCards
        : deckStudyCards.filter((studyCard) => studyCard.userGraphemePronunciationCard == null),
    [deckStudyCards, isPinyinFlashcardsEnabled],
  );

  // A front's stage spans ALL of its studied cards — including pronunciation cards the setting
  // above hides from the list — matching the batched stage query's semantics.
  const stageByFront = useMemo(() => deriveDeckStageByFront(deckStudyCards), [deckStudyCards]);

  // Only GRAPHEME subjects get card-filter facts — the facts query is keyed by grapheme, and a
  // word has none.
  const deckGraphemes = useMemo(
    () =>
      unfilteredStudyCardList.flatMap((studyCard) => {
        const subject = deckStudyCardSubject(studyCard);
        return subject?.kind === "grapheme" ? [subject.text] : [];
      }),
    [unfilteredStudyCardList],
  );
  const needsStaticFacts = requiresStaticCardFilterFacts(cardFilters);
  const factsByGrapheme = useCardFilterFactsByGrapheme(deckGraphemes, {
    enabled: needsStaticFacts,
  });

  const studyCardList = useMemo(() => {
    // While the catalog facts settle, show the deck unfiltered rather than flashing an empty deck.
    if (needsStaticFacts && factsByGrapheme === undefined) {
      return unfilteredStudyCardList;
    }
    return unfilteredStudyCardList.filter((studyCard) => {
      const subject = deckStudyCardSubject(studyCard);
      if (subject === undefined) {
        return true;
      }
      // The Words/Characters dimension applies to BOTH kinds and is decided from the card itself.
      if (!matchesCardFilterMorphemeKind(cardFilters, subject.kind)) {
        return false;
      }
      // Every remaining dimension is fact-driven, and facts are grapheme-only. A word card has
      // none, so gating it on them would hide every word whenever any level/tier filter is set.
      if (subject.kind === "word") {
        return true;
      }
      const facts = needsStaticFacts
        ? factsByGrapheme?.get(subject.text)
        : actorOnlyCardFilterFacts(stageByFront[subject.text]);
      return facts !== undefined && matchesCardFilters(cardFilters, facts, hanziScript);
    });
  }, [
    cardFilters,
    factsByGrapheme,
    hanziScript,
    needsStaticFacts,
    stageByFront,
    unfilteredStudyCardList,
  ]);

  return { stageByFront, studyCardList, unfilteredStudyCardList };
}
